import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/integrations/supabase/client';
import { getPlanFeatures, isUnlimited } from '@/constants/plans';
import { debugLogger } from '@/utils/debugLogger';

interface ConversationLimits {
  current: number;
  limit: number;
  remaining: number;
  canStart: boolean;
  isUnlimited: boolean;
}

export const useNewConversationLimits = () => {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const [conversationLimits, setConversationLimits] = useState<ConversationLimits>({
    current: 0,
    limit: 3,
    remaining: 3,
    canStart: true,
    isUnlimited: false
  });
  const [loading, setLoading] = useState(true);

  const checkConversationLimits = useCallback(async () => {
    if (!user?.id || !userProfile) return;

    try {
      setLoading(true);

      const planType = userProfile.plan_type || 'sponsor_free';
      const features = getPlanFeatures(planType);
      const dailyLimit = features.new_conversations_daily;

      // Count conversations started today (where user is initiator)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from('conversations')
        .select('*', { count: 'exact', head: true })
        .eq('user1_id', user.id)
        .gte('created_at', today.toISOString());

      if (error) {
        debugLogger.error('ConversationLimits', 'Error counting conversations:', error);
        return;
      }

      const currentCount = count || 0;
      const unlimited = isUnlimited(dailyLimit);
      const remaining = unlimited ? -1 : Math.max(0, dailyLimit - currentCount);
      const canStart = unlimited || remaining > 0;

      setConversationLimits({
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canStart,
        isUnlimited: unlimited
      });

      debugLogger.debug('ConversationLimits', 'Conversation limits updated:', {
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canStart,
        isUnlimited: unlimited
      });

    } catch (error) {
      debugLogger.error('ConversationLimits', 'Error checking conversation limits:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, userProfile]);

  const incrementConversationCount = useCallback(() => {
    setConversationLimits(prev => {
      if (prev.isUnlimited) return prev;
      
      const newCurrent = prev.current + 1;
      const newRemaining = Math.max(0, prev.limit - newCurrent);
      
      return {
        ...prev,
        current: newCurrent,
        remaining: newRemaining,
        canStart: newRemaining > 0
      };
    });
  }, []);

  useEffect(() => {
    checkConversationLimits();
  }, [checkConversationLimits]);

  return {
    conversationLimits,
    loading,
    checkConversationLimits,
    incrementConversationCount
  };
};
